/**
 * The one-line, plain-English result of a bulk import, e.g.
 *   "Imported 10 new (3 parents, 7 singles) · Updated 2 (2 variants) ·
 *    5 already up to date"
 *
 * Zero parts are left out, and so is a breakdown with only one kind in it
 * when that kind is singles (a "(10 singles)" after "10 new" says nothing).
 * Pure so the wording is pinned by tests.
 */
export interface KindCounts {
  parents: number;
  variants: number;
  singles: number;
}

export interface SummaryLineInput {
  added: number;
  updated: number;
  /** Existed; store data differs, curated values kept, nothing written. */
  kept: number;
  upToDate: number;
  /** Unticked in the preview. */
  skipped: number;
  failed: number;
  breakdown: { added: KindCounts; updated: KindCounts; kept: KindCounts };
}

const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

function kinds(c: KindCounts): string {
  const parts = [
    c.parents > 0 && n(c.parents, 'parent'),
    c.variants > 0 && n(c.variants, 'variant'),
    c.singles > 0 && n(c.singles, 'single'),
  ].filter((p): p is string => !!p);
  if (parts.length === 0) return '';
  if (parts.length === 1 && c.singles > 0) return '';
  return ` (${parts.join(', ')})`;
}

export function summaryLine(s: SummaryLineInput): string {
  const parts: string[] = [];
  if (s.added > 0) parts.push(`Imported ${s.added} new${kinds(s.breakdown.added)}`);
  if (s.updated > 0) parts.push(`Updated ${s.updated}${kinds(s.breakdown.updated)}`);
  if (s.kept > 0) parts.push(`Kept your edits on ${s.kept}${kinds(s.breakdown.kept)} (the store's data differs)`);
  if (s.upToDate > 0) parts.push(`${s.upToDate} already up to date`);
  if (s.skipped > 0) parts.push(`Skipped ${s.skipped} (unticked)`);
  if (s.failed > 0) parts.push(`${s.failed} failed`);
  return parts.length > 0 ? parts.join(' · ') : 'Nothing was imported.';
}
